/**
 * Frontend Sentry init (Phase 1D).
 *
 * Initialized from src/main.tsx before <App /> mounts. Reads the DSN
 * + sample rates from Vite env vars (`VITE_SENTRY_*`). When the DSN
 * is empty (dev / staging without a Sentry project) the init is a
 * no-op — `@sentry/react` is still imported so the surface available
 * to call sites (e.g. ErrorBoundary, captureException) keeps working.
 *
 * PII scrubbing matches the backend contract:
 *   * request body / query / cookies → never collected (we don't run
 *     the BrowserTracing fetch instrumentation in capture mode);
 *   * `email`, `name`, `phone`, `birth_date`, `document_number`,
 *     `iban`, `account_number` keys in any event extra / breadcrumb
 *     are replaced with `[scrubbed]` before transmission.
 *
 * The frontend doesn't load a Sentry SDK in development by default
 * (DSN typically blank); `initSentry()` returns a boolean so the
 * caller can branch if needed.
 */
import * as Sentry from '@sentry/react';

const SCRUB_KEYS = new Set([
  'password',
  'secret',
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'api_key',
  'apikey',
  'email',
  'phone',
  'birth_date',
  'document_number',
  'document_expiration',
  'iban',
  'account_number',
  'nfc_uid',
]);

function looksPii(key: string): boolean {
  const lower = (key || '').toLowerCase();
  // Normalize hyphenated header forms (X-API-KEY) into the SCRUB_KEYS
  // canonical underscore form so set membership succeeds.
  const normalized = lower.replace(/-/g, '_');
  if (SCRUB_KEYS.has(lower) || SCRUB_KEYS.has(normalized)) return true;
  return [
    'password', 'secret', 'token', 'authorization', 'cookie',
    'api_key', 'apikey', 'email',
  ].some((needle) => normalized.includes(needle));
}

function scrubMapping(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubMapping);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = looksPii(key) ? '[scrubbed]' : scrubMapping(val);
    }
    return out;
  }
  return value;
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Drop the user object's PII — keep id only.
  if (event.user) {
    event.user = { id: event.user.id };
  }
  // Scrub extras + breadcrumbs (the most common PII vector on the
  // frontend, e.g. a fetch breadcrumb capturing a bound auth header).
  if (event.extra) {
    event.extra = scrubMapping(event.extra) as Record<string, unknown>;
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      data: bc.data ? (scrubMapping(bc.data) as Record<string, unknown>) : bc.data,
    }));
  }
  // Defensive: if a request envelope ever lands here (BrowserTracing),
  // strip the body just like the backend does.
  if (event.request) {
    if ((event.request as { data?: unknown }).data) {
      (event.request as { data?: unknown }).data = '[scrubbed]';
    }
    if ((event.request as { cookies?: unknown }).cookies) {
      (event.request as { cookies?: unknown }).cookies = '[scrubbed]';
    }
  }
  return event;
}

export function initSentry(): boolean {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (!dsn) {
    return false;
  }

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)?.trim()
    || (import.meta.env.MODE as string)
    || 'development';

  const release = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim() || undefined;

  const tracesSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? '0.05',
  );

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    // Benign browser notice with no real exception behind it — the
    // observer simply deferred notifications to the next frame.
    ignoreErrors: [/ResizeObserver loop/],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.05,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubMapping(breadcrumb.data) as Record<string, unknown>;
      }
      return breadcrumb;
    },
  });
  return true;
}

// Re-export for call sites that want to wrap errors:
//   import { captureException } from '@/core/observability/sentry'
export const captureException = (error: unknown, context?: Record<string, unknown>) =>
  Sentry.captureException(error, context ? { extra: context } : undefined);

export { scrubMapping as _scrubMapping, looksPii as _looksPii, beforeSend as _beforeSend };
